import React, { useEffect, useState } from 'react';

const ContractTimer = ({ 
    packetDetails, 
    contractData,
    onContractExpire 
}) => {
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [isExpired, setIsExpired] = useState(false);
    const [shouldShowTimer, setShouldShowTimer] = useState(false);
    const [hasCalledExpire, setHasCalledExpire] = useState(false); // Add this state


    // Parse expiration days and time from contract data
    const parseExpirationDetails = () => {
        const daysMatch = contractData.data.dateFields.acceptance_deadline_date.daysFromMEC.match(/(\d+) day\/s from receiving contract/);
        const expirationDays = daysMatch ? parseInt(daysMatch[1]) : 3; // Default to 3 days
        const timeMatch = contractData.data.dateFields.acceptance_deadline_time.daysFromMEC.match(/(\d{1,2}):(\d{2}) (AM|PM)/);
        
        let expirationTime = '23:59:59'; // Default to 11:59 PM
    
        if (timeMatch) {
            let [ , hours, minutes, period ] = timeMatch;
            
            hours = parseInt(hours);
            minutes = parseInt(minutes);
    
            if (period === 'PM' && hours !== 12) {
                hours += 12;
            } else if (period === 'AM' && hours === 12) {
                hours = 0;
            }
    
            expirationTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
            
        }

        return { expirationDays, expirationTime };
    };


    const formatTime = (totalSeconds) => {
        const days = Math.floor(totalSeconds / (24 * 3600));
        const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
    
        if (days === 0 && hours === 0) {
            return `${minutes}m ${seconds}s`;
        }
    
        if (days === 0) {
            return `${hours}h ${minutes}m`;
        }
    
        return `${days}d ${hours}h`;
    };

    const getStartTime = () => {
        // Check if packetDetails is undefined, null, or completed
        if (!contractData?.packetDetails || contractData?.packetDetails?.status === 'completed') {
            return null;
        }
    
        // Check if all buyers have completed
        const signers = contractData?.packetDetails?.documentGroup?.signers || [];
        const buyerSigners = signers.filter(signer => signer.aliasId.startsWith('buyer'));
        const allBuyersCompleted = buyerSigners.every(buyer => buyer.status === 'completed');
    
        if (!allBuyersCompleted) {
            return null;
        }
    
        const { expirationDays, expirationTime } = parseExpirationDetails();
    
        const buyerCompletedTimes = signers
            .filter(signer => 
                signer.aliasId?.startsWith('buyer') && 
                signer.status === 'completed'
            )
            .map(buyer => {
                const time = new Date(contractData?.packetDetails?.updatedAt || contractData?.packetDetails?.createdAt);
                const denverTime = new Date(time.toLocaleString('en-US', { timeZone: 'America/Denver' }));
                return denverTime;
            });
    
        const latestBuyerTime = contractData?.packetDetails?.updatedAt 
            ? new Date(contractData?.packetDetails?.updatedAt) 
            : new Date(contractData?.packetDetails?.createdAt);
    
        const expirationDateTime = new Date(latestBuyerTime);
        expirationDateTime.setDate(expirationDateTime.getDate() + expirationDays);
    
        // Set the expiration time to the parsed time
        const [hours, minutes, period] = expirationTime.split(/[:\s]/);
        expirationDateTime.setHours(period === 'PM' ? parseInt(hours) + 12 : parseInt(hours));
        expirationDateTime.setMinutes(parseInt(minutes));
        expirationDateTime.setSeconds(0);
        expirationDateTime.setMilliseconds(0);
    
        return expirationDateTime;
    };
    
    useEffect(() => {
        // Stop if no packet details or contract is completed
        if (!contractData?.packetDetails || contractData?.packetDetails?.status === 'completed' || contractData?.packetDetails?.status === 'draft') {
            setIsExpired(false);
            setTimeRemaining(0);
            setShouldShowTimer(false);
            return;
        }
    
        const startTime = getStartTime();
        
        // If no start time, don't start timer
        if (!startTime) {
            setShouldShowTimer(false);
            return;
        }
    
        setShouldShowTimer(true);
    
        const calculateRemainingTime = () => {
            const now = new Date();
            const remainingMs = startTime.getTime() - now.getTime();
            
            if (remainingMs <= 0) {
                setIsExpired(true);
                setTimeRemaining(0);
                if (!hasCalledExpire) {
                    setHasCalledExpire(true);
                    onContractExpire(packetDetails);
                }
                return;
            }
    
            setTimeRemaining(Math.floor(remainingMs / 1000));
        };
    
        // Initial calculation
        calculateRemainingTime();
    
        // Update every second
        const timerId = setInterval(calculateRemainingTime, 1000);
    
        // Cleanup interval on unmount
        return () => clearInterval(timerId);
    }, [packetDetails, contractData, hasCalledExpire, contractData?.packetDetails?.updatedAt]);

    // Only render if timer should be shown
    if (!shouldShowTimer) return null;

    return (
        shouldShowTimer && (
        <div className={`contract-timer ${isExpired ? 'expired' : ''}`}>
            {!isExpired &&(
                <div className='inline-flex gap-1 w-full items-start'>
                    <div className='text-slate-600 text-[13px]'>Expires:</div>
                    <div className="text-[13px]">
                        {formatTime(timeRemaining)}
                    </div>
                </div>
            )}
        </div>
        )
    );
};

export default ContractTimer;